import { useState } from 'react';
import {
    ValidationName,
    ValidationRule,
    validationRules
} from '../components/CustomFormsComponents/hooks/useValidation';
import { FieldConfig } from '../components/CustomFormsComponents/CustomField';
import { useTranslation } from 'react-i18next';

interface FormErrors {
    [key: string]: { hasError: boolean; errorMessage: string };
}

const useFormValidation = () => {
    const [errors, setErrors] = useState<FormErrors>({});
    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const { t } = useTranslation('validations');

    const validateField = (name: string, value: any, validations: ValidationRule[]) => {
        const newErrors: FormErrors = {};
        let hasError = false;

        validations.forEach((validation) => {
            const validationFn = validationRules(t)[validation.name as ValidationName];
            if (validationFn) {
                const error = validationFn(value, validation.param);
                if (error) {
                    newErrors[name] = { hasError: true, errorMessage: error };
                    hasError = true;
                }
            }
        });

        setErrors((prevErrors) => ({
            ...prevErrors,
            ...newErrors
        }));
        setHasValidationErrors(hasError);
    };

    const validateForm = (formData: any, fieldsConfig: FieldConfig[]): boolean => {
        const newErrors: FormErrors = {};
        let hasError = false;

        fieldsConfig.forEach((field) => {
            const validationsToApply = field.validations || [];

            validationsToApply.forEach((validation) => {
                const validationFn = validationRules(t)[validation.name as ValidationName];
                if (validationFn) {
                    const fieldValue = formData[field.name as keyof FormData];

                    if (Array.isArray(fieldValue) && fieldValue.length === 0) {
                        newErrors[field.name] = {
                            hasError: true,
                            errorMessage: t('validation.required')
                        };
                        hasError = true;
                    } else {
                        const error = validationFn(fieldValue, validation.param);
                        if (error) {
                            newErrors[field.name] = { hasError: true, errorMessage: error };
                            hasError = true;
                        }
                    }
                }
            });
        });

        setErrors(newErrors);
        setHasValidationErrors(hasError);

        return !hasError;
    };

    const handleBlurValidation = (name: string, hasError: boolean, errorText: string) => {
        setErrors((prevErrors) => {
            const updatedErrors = {
                ...prevErrors,
                [name]: { hasError, errorMessage: errorText }
            };

            const hasErrors = Object.values(updatedErrors).some((error) => error.hasError);
            setHasValidationErrors(hasErrors);

            return updatedErrors;
        });
    };

    const resetValidation = () => {
        setErrors({});
        setHasValidationErrors(false);
    };

    return {
        errors,
        hasValidationErrors,
        validateField,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    };
};

export default useFormValidation;
